import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import CampaignCta from "@/components/CampaignCta";
import FaqAccordion, { type FaqAccordionItem } from "@/components/FaqAccordion";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { trackCtaClick } from "@/lib/analytics";
import {
  AlertTriangle,
  History,
  HelpCircle,
  KeyRound,
  CalendarClock,
  RotateCcw,
  CheckCircle2,
} from "lucide-react";

const problems = [
  {
    icon: <AlertTriangle className="h-8 w-8 text-primary" />,
    title: "One delete, gone forever",
    text: "Jotform has no undo across a busy book of client work. A wrong click can erase weeks of build.",
  },
  {
    icon: <History className="h-8 w-8 text-primary" />,
    title: "Junior edits break senior work",
    text: "Without version history, a small change by a new team member is hard to find and hard to roll back.",
  },
  {
    icon: <HelpCircle className="h-8 w-8 text-primary" />,
    title: '"When did this change?"',
    text: "Client questions about a form change need a real record, not a guess from memory.",
  },
];

const steps = [
  {
    icon: <KeyRound className="h-8 w-8 text-primary" />,
    title: "Connect Jotform",
    text: "Link a client's Jotform account with an API token. No installs, no agents.",
  },
  {
    icon: <CalendarClock className="h-8 w-8 text-primary" />,
    title: "Daily snapshots",
    text: "FAB backs up every form each day — fields, logic, conditions, and theme.",
  },
  {
    icon: <RotateCcw className="h-8 w-8 text-primary" />,
    title: "Restore in one click",
    text: "Restore any saved version, and review backup history with a timestamp for every form.",
  },
];

const useCases = [
  {
    title: "Client form portfolios",
    text: "Safeguard the forms behind client campaigns, intake, and lead capture.",
  },
  {
    title: "Junior staff training",
    text: "Let newer team members work knowing every form can be rolled back.",
  },
  {
    title: "Audits and change requests",
    text: 'Answer "what changed, and when" with daily snapshots.',
  },
  {
    title: "Agency-to-client handoffs",
    text: "Hand over a clean, backed-up form set at the end of an engagement.",
  },
];

const faqItems: FaqAccordionItem[] = [
  {
    question: "Is FAB affiliated with Jotform?",
    answer:
      "No. FAB is an independent third-party tool that uses the public Jotform API to back up form configuration.",
  },
  {
    question: "Does FAB back up form submissions or respondent data?",
    answer:
      "No. FAB backs up the form itself — fields, logic, conditions, and theme. Submissions stay in Jotform.",
  },
  {
    question: "How do I cover more than one client's Jotform account?",
    answer:
      "Each FAB workspace connects to one Jotform account. To cover several client accounts, set up a separate workspace per account. Contact us at support@fab.day and we'll help you plan the setup.",
  },
  {
    question: "How often are backups taken?",
    answer: "Daily, automatically. You can also take a manual snapshot any time.",
  },
  {
    question: "What happens when a client offboards?",
    answer:
      "Download the backups as JSON for your records or to hand off, then disconnect the account.",
  },
];

const AgenciesPage = () => {
  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Navbar />
      <main className="flex-grow">
        {/* Hero */}
        <section className="bg-gradient-to-br from-primary to-primary/80 dark:from-primary/90 dark:to-primary/70 text-white py-20 md:py-28">
          <div className="container mx-auto px-6 max-w-4xl text-center">
            <h1 className="text-4xl md:text-5xl font-bold tracking-tight">
              Protect every Jotform form you manage for clients.
            </h1>
            <p className="mt-6 text-lg md:text-xl max-w-3xl mx-auto leading-relaxed text-white/90">
              FAB is the independent backup and restore layer for agencies running Jotform.
              Daily snapshots, full version history, and one-click recovery for the forms
              your team builds and maintains.
            </p>
            <div className="mt-9 flex flex-wrap gap-3 justify-center">
              <CampaignCta
                location="agencies-hero"
                className="bg-white text-primary hover:bg-gray-100 dark:bg-gray-900 dark:text-white dark:hover:bg-gray-800 font-semibold px-8"
              >
                Start free 14-day trial
              </CampaignCta>
              <Button
                asChild
                size="lg"
                variant="outline"
                className="border-white text-white hover:bg-white/10 dark:border-gray-700"
              >
                <a
                  href="#how-it-works"
                  onClick={() => trackCtaClick("agencies-hero-secondary", "#how-it-works")}
                >
                  See how it works
                </a>
              </Button>
            </div>
            <p className="mt-6 text-xs text-white/70">
              Independent third-party tool. Not affiliated with Jotform.
            </p>
          </div>
        </section>

        {/* Trust strip */}
        <section className="py-6 border-b bg-muted/30">
          <p className="container mx-auto px-6 text-center text-sm text-muted-foreground">
            Built for agencies and consultancies that run Jotform for their clients.
          </p>
        </section>

        {/* Problem block */}
        <section className="py-16 md:py-20">
          <div className="container mx-auto px-6 max-w-5xl">
            <h2 className="text-3xl font-bold text-foreground text-center mb-12">
              The agency form problem
            </h2>
            <div className="grid gap-6 md:grid-cols-3">
              {problems.map((item) => (
                <Card key={item.title}>
                  <CardHeader>
                    {item.icon}
                    <CardTitle className="text-xl">{item.title}</CardTitle>
                  </CardHeader>
                  <CardContent className="text-muted-foreground">{item.text}</CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Solution block */}
        <section id="how-it-works" className="py-16 md:py-20 bg-muted/30 scroll-mt-20">
          <div className="container mx-auto px-6 max-w-5xl">
            <h2 className="text-3xl font-bold text-foreground text-center mb-12">
              How FAB fits an agency workflow
            </h2>
            <div className="grid gap-8 md:grid-cols-3">
              {steps.map((item, index) => (
                <div key={item.title} className="text-center px-4">
                  <div className="flex justify-center mb-4">{item.icon}</div>
                  <div className="text-sm font-semibold text-primary mb-1">
                    Step {index + 1}
                  </div>
                  <h3 className="text-xl font-semibold text-foreground mb-2">
                    {item.title}
                  </h3>
                  <p className="text-muted-foreground">{item.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Use cases */}
        <section className="py-16 md:py-20">
          <div className="container mx-auto px-6 max-w-5xl">
            <h2 className="text-3xl font-bold text-foreground text-center mb-12">
              Where agencies use FAB
            </h2>
            <div className="grid gap-6 sm:grid-cols-2">
              {useCases.map((item) => (
                <div key={item.title} className="flex gap-3">
                  <CheckCircle2 className="h-6 w-6 text-green-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-foreground">{item.title}</h3>
                    <p className="text-muted-foreground text-sm mt-1">{item.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Pricing teaser */}
        <section className="py-14 bg-muted/30 border-y">
          <div className="container mx-auto px-6 max-w-3xl text-center">
            <p className="text-lg text-foreground">
              Plans start at <span className="font-semibold">$8/month</span>, matched to
              your Jotform plan.
            </p>
            <div className="mt-5">
              <Button asChild size="lg" variant="outline">
                <Link
                  to="/pricing"
                  onClick={() => trackCtaClick("agencies-pricing", "/pricing")}
                >
                  See pricing
                </Link>
              </Button>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <FaqAccordion
          title="Agency questions about Jotform backup"
          description="Short answers for agencies weighing an independent backup layer for client forms."
          items={faqItems}
        />

        {/* Final CTA band */}
        <section className="py-16 md:py-20 bg-primary dark:bg-primary/90 text-white">
          <div className="container mx-auto px-6 text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-8">
              Stop rebuilding client forms from scratch.
            </h2>
            <CampaignCta
              location="agencies-footer"
              className="bg-white text-primary hover:bg-gray-100 dark:bg-gray-900 dark:text-white dark:hover:bg-gray-800 font-semibold px-8"
            >
              Start free 14-day trial
            </CampaignCta>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default AgenciesPage;
